import { createStore, get, set, clear, type UseStore } from "idb-keyval";
import { readBypassCacheFromLocalStorage } from "~/composables/usePwaCacheFlag";

export type CacheVersion = string | null;

// Self-describing envelope. The DB name (`general_{customerId}`) already
// isolates tenants, but stamping resource + customerId inside the entry lets
// us detect tampering/cross-tenant leaks on read and makes raw IDB inspection
// easy.
export type CacheEnvelope<T> = {
  resource: string;
  customerId: string;
  version: CacheVersion;
  data: T;
  updatedAt: number;
};

// Legacy shape (no `resource`/`customerId`) written by v1. Detected on read.
type LegacyCacheEntry<T> = {
  data: T;
  updatedAt: number;
  version?: CacheVersion;
};

// One IndexedDB database per customer (storeId). The Niceloop POS can be used
// by multiple shops on the same device (login/logout), so isolating data at
// the DB level keeps caches from leaking across tenants and avoids manual
// cleanup on user switch.
const storeCache = new Map<string, UseStore>();

function getStoreFor(customerId: string): UseStore {
  const dbName = `general_${customerId}`;
  let s = storeCache.get(dbName);
  if (!s) {
    s = createStore(dbName, "caches");
    storeCache.set(dbName, s);
  }
  return s;
}

function isEnvelope<T>(value: unknown): value is CacheEnvelope<T> {
  return (
    !!value &&
    typeof value === "object" &&
    "resource" in (value as Record<string, unknown>) &&
    "customerId" in (value as Record<string, unknown>) &&
    "data" in (value as Record<string, unknown>)
  );
}

export async function readCacheEntry<T>(
  customerId: string,
  resource: string
): Promise<CacheEnvelope<T> | null> {
  // Feature-flag bypass: the `pwaDataCacheFlag` localStorage entry (managed by
  // `usePwaCacheFlag`) lets admins force fresh fetches for debugging or for
  // shops that opted out of caching. When set, every cache read is reported as
  // a miss so callers refetch from the network. Writes still happen so the
  // cache stays warm if the flag is flipped back later.
  if (readBypassCacheFromLocalStorage()) return null;
  return readCacheEntryRaw<T>(customerId, resource);
}

// `readCacheEntryRaw` ignores the bypass flag. Reserved for resilience paths
// (e.g. versionSync's refetch-failure fallback) that need to serve stale data
// when the network is dead — without it, bypass mode would brick the boot on
// any Firestore/API outage.
export async function readCacheEntryRaw<T>(
  customerId: string,
  resource: string
): Promise<CacheEnvelope<T> | null> {
  if (!customerId) return null;
  try {
    const raw = await get<unknown>(resource, getStoreFor(customerId));
    if (raw == null) return null;

    if (isEnvelope<T>(raw)) {
      // Defense-in-depth: refuse to return entries that don't match the
      // current resource/tenant. Treat as miss.
      if (raw.resource !== resource || raw.customerId !== customerId) {
        console.warn(
          `[idbCache:${resource}] envelope mismatch (resource=${raw.resource}, customerId=${raw.customerId})`
        );
        return null;
      }
      return raw;
    }

    // Legacy v1 entry — synthesize an envelope with a null version so the
    // next reconcile treats it as stale and refetches.
    const legacy = raw as LegacyCacheEntry<T>;
    return {
      resource,
      customerId,
      version: legacy.version ?? null,
      data: legacy.data,
      updatedAt: legacy.updatedAt ?? 0,
    };
  } catch (err) {
    console.warn(`[idbCache:${resource}] read failed`, err);
    return null;
  }
}

export async function readCache<T>(
  customerId: string,
  resource: string
): Promise<T | null> {
  const entry = await readCacheEntry<T>(customerId, resource);
  return entry?.data ?? null;
}

export async function writeCache<T>(
  customerId: string,
  resource: string,
  data: T,
  version: CacheVersion = null
): Promise<void> {
  if (!customerId) return;
  try {
    const envelope: CacheEnvelope<T> = {
      resource,
      customerId,
      version,
      data,
      updatedAt: Date.now(),
    };
    await set(resource, envelope, getStoreFor(customerId));
  } catch (err) {
    console.warn(`[idbCache:${resource}] write failed`, err);
  }
}

export async function clearCacheForCustomer(customerId: string): Promise<void> {
  if (!customerId) return;
  try {
    await clear(getStoreFor(customerId));
  } catch (err) {
    console.warn(`[idbCache] clear failed`, err);
  }
}
